import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
} from "@mui/material";
import { getProgramsList, getCoursesList } from "../../pages/marketing/marketingApi";

const REWARD_TYPES = ["Cash", "Gift", "Points", "Commission Boost"];
const SCOPES = [
  { value: "overall", label: "Overall" },
  { value: "program", label: "Program" },
  { value: "course", label: "Course" },
];

export default function CreateBountyModal({ open, onClose, onSubmit }) {
  const [saving, setSaving] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [courses, setCourses] = useState([]);
  const [form, setForm] = useState({
    name: "",
    description: "",
    reward_type: "Cash",
    reward_value: "",
    conversions_required: "1",
    expires_at: "",
    scope_type: "overall",
    scope_id: "",
  });

  useEffect(() => {
    if (!open) return;
    getProgramsList().then(setPrograms).catch(() => setPrograms([]));
  }, [open]);

  useEffect(() => {
    if (form.scope_type === "program" && form.scope_id) {
      getCoursesList(Number(form.scope_id)).then(setCourses).catch(() => setCourses([]));
    } else if (form.scope_type === "course") {
      getCoursesList(null).then(setCourses).catch(() => setCourses([]));
    } else {
      setCourses([]);
    }
  }, [form.scope_type, form.scope_id]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "scope_type" ? { scope_id: "" } : {}),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = (form.name || "").trim();
    const value = parseFloat(form.reward_value);
    const conversionsRequired = Math.max(1, parseInt(form.conversions_required, 10) || 1);
    if (!name) return;
    if (isNaN(value) || value < 0) return;
    if (form.scope_type === "program" && !form.scope_id) return;
    if (form.scope_type === "course" && !form.scope_id) return;

    setSaving(true);
    try {
      await onSubmit({
        name,
        description: (form.description || "").trim(),
        reward_type: form.reward_type,
        reward_value: value,
        conversions_required: conversionsRequired,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        scope_type: form.scope_type,
        scope_id: form.scope_type === "overall" ? null : form.scope_id,
      });
      setForm({
        name: "",
        description: "",
        reward_type: "Cash",
        reward_value: "",
        conversions_required: "1",
        expires_at: "",
        scope_type: "overall",
        scope_id: "",
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ fontWeight: 600 }}>Create Bounty (Conversion)</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <TextField
            fullWidth
            label="Bounty Name"
            value={form.name}
            onChange={handleChange("name")}
            required
            margin="normal"
            size="small"
          />
          <TextField
            fullWidth
            label="Description"
            value={form.description}
            onChange={handleChange("description")}
            multiline
            rows={2}
            margin="normal"
            size="small"
            placeholder="e.g. 1 conversion in this scope"
          />
          <FormControl fullWidth size="small" margin="normal">
            <InputLabel>Scope</InputLabel>
            <Select value={form.scope_type} label="Scope" onChange={handleChange("scope_type")}>
              {SCOPES.map(({ value, label }) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
            <Box component="span" sx={{ fontSize: "0.75rem", color: "text.secondary", mt: 0.5, display: "block" }}>
              Conversion bounty for overall, a program, or a course.
            </Box>
          </FormControl>
          {form.scope_type === "program" && (
            <FormControl fullWidth size="small" margin="normal" required>
              <InputLabel>Program</InputLabel>
              <Select value={form.scope_id} label="Program" onChange={handleChange("scope_id")}>
                <MenuItem value="">Select program</MenuItem>
                {programs.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {form.scope_type === "course" && (
            <FormControl fullWidth size="small" margin="normal" required>
              <InputLabel>Course</InputLabel>
              <Select value={form.scope_id} label="Course" onChange={handleChange("scope_id")}>
                <MenuItem value="">Select course</MenuItem>
                {courses.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl fullWidth size="small" margin="normal">
            <InputLabel>Reward Type</InputLabel>
            <Select value={form.reward_type} label="Reward Type" onChange={handleChange("reward_type")}>
              {REWARD_TYPES.map((t) => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Conversions needed to win"
            type="number"
            inputProps={{ min: 1, step: 1 }}
            value={form.conversions_required}
            onChange={handleChange("conversions_required")}
            margin="normal"
            size="small"
            helperText="Number of conversions required. Auto-checked when conversions are recorded."
            placeholder="e.g. 3"
          />
          <TextField
            fullWidth
            label="Reward Value"
            type="number"
            inputProps={{ min: 0, step: 1 }}
            value={form.reward_value}
            onChange={handleChange("reward_value")}
            margin="normal"
            size="small"
            placeholder="e.g. 50"
          />
          <TextField
            fullWidth
            label="Expiration (optional)"
            type="datetime-local"
            value={form.expires_at}
            onChange={handleChange("expires_at")}
            margin="normal"
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving || !form.name.trim() || (form.scope_type !== "overall" && !form.scope_id)}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {saving ? "Creating…" : "Create Bounty"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
